import React, { useState, useEffect, useCallback, useRef } from "react";

import { CloseIcon, ErrorCircleIcon, SuccessCheckIcon } from "../../components/Icons";

export type ToastType = "default" | "success" | "error" | "info" | "warning";

export interface ToastProps {
  message: string;
  type?: ToastType;
  materialIcon?: string;
  disableAutoClose?: boolean;
  showCloseButton?: boolean;
  duration?: number;
  onClose?: () => void;
  action?: {
    label: string;
    onClick: () => void;
  };
  position?: "top" | "bottom";
  /**
   * Identity of this toast instance. Pass a value that changes per shown toast (e.g. the props
   * object held in state) so a REPLACEMENT toast restarts the lifecycle/countdown even when its
   * visible content is identical (e.g. two back-to-back "Theme updated" commits).
   */
  contentKey?: unknown;
}

export const Toast: React.FC<ToastProps> = ({
  message,
  type = "default",
  materialIcon,
  disableAutoClose = false,
  showCloseButton,
  duration = 3000,
  onClose,
  action,
  position = "top",
  contentKey,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isEntering, setIsEntering] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  // If disableAutoClose is true, we have to show the close button
  showCloseButton = !!showCloseButton || disableAutoClose;

  const animationDuration = 300;

  // Pending dismiss-animation timer. Tracked so a REPLACEMENT toast can cancel it: an
  // action click (e.g. the refresh retry) can synchronously produce a new toast, and the
  // old dismiss's onClose would otherwise fire 300ms later and clear the replacement.
  const exitAnimationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeToast = useCallback(() => {
    setIsExiting(true);
    // A repeat dismiss (double-click, or ✕ while already fading) must not orphan the
    // prior timer — an orphan would still fire onClose and could clear a replacement.
    if (exitAnimationTimerRef.current != null) {
      clearTimeout(exitAnimationTimerRef.current);
    }
    exitAnimationTimerRef.current = setTimeout(() => {
      exitAnimationTimerRef.current = null;
      setIsVisible(false);
      onClose?.();
    }, animationDuration); // Match animation duration
  }, [onClose]);

  // Latest closeToast without it being an effect dep: parents often pass a fresh inline `onClose`
  // every render, and restarting the dismiss timer on each re-render would keep the toast alive
  // indefinitely while unrelated state churns (e.g. opening the theme selector).
  const closeToastRef = useRef(closeToast);
  closeToastRef.current = closeToast;

  useEffect(() => {
    // (Re)start the lifecycle when the CONTENT changes — the same mounted instance can receive a
    // replacement toast, which must reset visibility and get its own full countdown.
    setIsVisible(true);
    setIsExiting(false);
    setIsEntering(true);

    // Trigger entrance animation
    const enterTimer = setTimeout(() => {
      setIsEntering(false);
    }, 10); // Small delay to ensure initial state is rendered

    // Trigger automatic dismiss animation
    const exitTimer = disableAutoClose ? null : setTimeout(() => closeToastRef.current(), duration);

    return () => {
      clearTimeout(enterTimer);
      if (exitTimer != null) {
        clearTimeout(exitTimer);
      }
      // Cancel an in-flight dismiss animation so its onClose can't clear the replacement.
      if (exitAnimationTimerRef.current != null) {
        clearTimeout(exitAnimationTimerRef.current);
        exitAnimationTimerRef.current = null;
      }
    };
  }, [message, duration, disableAutoClose, contentKey]);

  if (!isVisible) {
    return null;
  }

  const typeStyles: Record<ToastType, string> = {
    default: "bg-main-background text-main-foreground border border-card-border",
    success: "bg-main-background text-main-foreground border border-card-border",
    error: "bg-[#de1135] text-white",
    info: "bg-main-foreground text-main-background border border-card-border",
    warning: "bg-amber-500/95 text-gray-900 border border-amber-400/30",
  };

  // Error toasts follow the Figma "Notification/Error" spec (red pill, semibold 14px
  // copy, white squared action button); other types keep the original layout.
  const isError = type === "error";

  // Animation direction depends on position - top toasts slide down, bottom toasts slide up
  const animationClass = isExiting
    ? `opacity-0 ${position === "bottom" ? "translate-y-2" : "-translate-y-2"}`
    : isEntering
    ? `opacity-0 ${position === "bottom" ? "translate-y-2" : "-translate-y-2"}`
    : "opacity-100 translate-y-0";

  return (
    <div
      className={`${
        isError ? "px-6 py-3" : "px-3 py-3"
      } rounded-lg shadow-lg transition-all duration-${animationDuration} ease-in-out transform ${
        typeStyles[type]
      } ${animationClass}`}
    >
      <div className={`flex items-center ${isError ? "gap-3" : "gap-2"}`}>
        {materialIcon && (
          <span
            className={`material-icons ${isError ? "" : "ml-2"}`}
            style={{ fontSize: isError ? "18px" : "20px" }}
          >
            {materialIcon}
          </span>
        )}
        {isError && !materialIcon && <ErrorCircleIcon className="shrink-0" />}
        {type === "success" && !materialIcon && <SuccessCheckIcon />}
        <span
          className={
            isError ? "text-sm font-semibold leading-[18px] max-w-md" : "text-base mx-3 my-0.5"
          }
        >
          {message}
        </span>

        {action && (
          <button
            onClick={() => {
              action.onClick();
              closeToast();
            }}
            className={
              isError
                ? "ml-3 h-8 px-3 text-sm font-semibold rounded cursor-pointer transition-colors bg-white text-gray-900 hover:opacity-90 whitespace-nowrap shrink-0"
                : "ml-8 mr-2 px-4 py-1.5 text-sm font-semibold rounded-full [corner-shape:squircle] cursor-pointer transition-colors bg-main-background text-main-foreground hover:opacity-80"
            }
          >
            {action.label}
          </button>
        )}

        {showCloseButton &&
          (isError ? (
            <button
              onClick={closeToast}
              className="bg-white/30 hover:bg-white/40 rounded-full transition-colors flex items-center justify-center w-5 h-5 shrink-0"
              aria-label="Close"
            >
              <CloseIcon width={8} height={8} />
            </button>
          ) : (
            <button
              onClick={closeToast}
              className="opacity-70 hover:bg-white/20 rounded-full transition-colors flex items-center justify-center p-1"
              aria-label="Close"
            >
              <span className="material-icons" style={{ fontSize: "20px" }}>
                close
              </span>
            </button>
          ))}
      </div>
    </div>
  );
};
